import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { FilterSidebar, type FilterGroup } from "@/components/filter-sidebar";
import { ListToolbar } from "@/components/list-toolbar";
import { PaginationBar } from "@/components/pagination-bar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useList } from "@/hooks/use-list";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth-store";
import { Plus, Trash2, Check } from "lucide-react";
import { cn } from "@/lib/cn";

type Filter = "all" | "open" | "mine" | "today" | "overdue" | "completed" | "high";

export default function TasksPage() {
  const me = useAuthStore((s) => s.me);
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("dueDate");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [filter, setFilter] = useState<Filter>("open");
  const [filtersOpen, setFiltersOpen] = useState(true);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (filter === "open")      p.open = true;
    if (filter === "today")     p.dueToday = true;
    if (filter === "overdue")   p.overdue = true;
    if (filter === "completed") p.status = "COMPLETED";
    if (filter === "high")      p.priority = "HIGH";
    if (filter === "mine" && me) p.userId = me.id;
    return p;
  }, [filter, me]);

  const { data, isLoading } = useList<any>("/tasks", {
    q, page, pageSize, sortBy, sortDir, ...params,
  });

  const rows = data?.data ?? [];
  const total = data?.pagination?.total;

  const groups: FilterGroup[] = [
    {
      title: "Views",
      items: [
        { label: "Open tasks",     active: filter === "open",      onClick: () => setFilter("open") },
        { label: "My tasks",       active: filter === "mine",      onClick: () => setFilter("mine") },
        { label: "Due today",      active: filter === "today",     onClick: () => setFilter("today") },
        { label: "Overdue",        active: filter === "overdue",   onClick: () => setFilter("overdue") },
        { label: "Completed",      active: filter === "completed", onClick: () => setFilter("completed") },
        { label: "All",            active: filter === "all",       onClick: () => setFilter("all") },
      ],
    },
    {
      title: "Priority",
      items: [
        { label: "High only",      active: filter === "high",      onClick: () => setFilter("high") },
      ],
    },
  ];

  async function toggleComplete(t: any) {
    const newStatus = t.status === "COMPLETED" ? "NOT_STARTED" : "COMPLETED";
    await api.put(`/tasks/${t.id}`, { status: newStatus });
    qc.invalidateQueries({ queryKey: ["/tasks"] });
  }

  async function del(id: string, subject: string) {
    if (!confirm(`Delete task "${subject}"?`)) return;
    await api.delete(`/tasks/${id}`);
    qc.invalidateQueries({ queryKey: ["/tasks"] });
  }

  return (
    <>
      <PageHeader
        title="Tasks"
        description="Follow-ups, to-dos, reminders — from any record or standalone."
        actions={<Button asChild><Link to="/tasks/new"><Plus className="mr-1 h-4 w-4"/>New Task</Link></Button>}
      />

      <ListToolbar
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        sortBy={sortBy}
        sortDir={sortDir}
        sortOptions={[
          { value: "dueDate",   label: "Due date" },
          { value: "createdAt", label: "Created" },
          { value: "priority",  label: "Priority" },
        ]}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        rightSlot={<Input placeholder="Search tasks…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-8 w-64" />}
      />

      <div className="flex flex-1 min-h-[60vh]">
        {filtersOpen && <FilterSidebar groups={groups} onClear={() => setFilter("all")} />}

        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex-1 overflow-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-10"></Th>
                  <Th>Subject</Th>
                  <Th>Due Date</Th>
                  <Th>Status</Th>
                  <Th>Priority</Th>
                  <Th>Related to</Th>
                  <Th>Owner</Th>
                  <Th className="w-10"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading && <Tr><Td colSpan={8} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
                {!isLoading && rows.length === 0 && (
                  <Tr><Td colSpan={8} className="py-12 text-center text-muted-foreground">No tasks in this view.</Td></Tr>
                )}
                {rows.map((t: any) => {
                  const done = t.status === "COMPLETED";
                  const overdue = !done && t.scheduledAt && new Date(t.scheduledAt) < new Date();
                  return (
                    <Tr key={t.id} className="group">
                      <Td>
                        <button
                          onClick={() => toggleComplete(t)}
                          title={done ? "Mark as not started" : "Mark as completed"}
                          className={cn(
                            "flex h-5 w-5 items-center justify-center rounded border",
                            done ? "border-primary bg-primary text-primary-foreground" : "border-input hover:bg-muted",
                          )}
                        >
                          {done && <Check className="h-3 w-3" />}
                        </button>
                      </Td>
                      <Td>
                        <span className={cn("font-medium", done && "text-muted-foreground line-through")}>
                          {t.subject}
                        </span>
                      </Td>
                      <Td className={cn("text-sm", overdue && "text-destructive")}>
                        {t.scheduledAt ? new Date(t.scheduledAt).toLocaleDateString() : "—"}
                      </Td>
                      <Td><Badge variant="secondary">{(t.status ?? "NOT_STARTED").replace(/_/g, " ")}</Badge></Td>
                      <Td>{t.priority && (
                        <Badge variant={t.priority === "HIGH" ? "destructive" : t.priority === "LOW" ? "secondary" : "default"}>
                          {t.priority}
                        </Badge>
                      )}</Td>
                      <Td className="text-muted-foreground">
                        {t.contact?.name ? <Link to={`/contacts/${t.contact.id}`} className="hover:underline">{t.contact.name}</Link> :
                         t.deal?.name    ? <Link to={`/deals`} className="hover:underline">{t.deal.name}</Link> : "—"}
                      </Td>
                      <Td className="text-muted-foreground">{t.user?.name ?? "—"}</Td>
                      <Td>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => del(t.id, t.subject)} title="Delete" className="flex h-7 w-7 items-center justify-center rounded-md text-destructive hover:bg-muted">
                            <Trash2 className="h-3.5 w-3.5"/>
                          </button>
                        </div>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </div>

          <PaginationBar
            page={page} pageSize={pageSize} total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      </div>
    </>
  );
}
